import { flow, isEqual, isNull, isUndefined, unset } from 'lodash/fp';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { toast } from 'react-toastify';

import { deleteCalculatorPrompt, fetchCalculatorPrompts, updateCalculatorPromptPriorityOrder, upsertCalculatorFieldType, upsertCalculatorPadOption, upsertCalculatorPrompt } from '../../../../services/calculator';
import { deleteCalculatorPromptFailed, deleteCalculatorPromptSuccessful, fetchCalculatorPromptsFailed, fetchCalculatorPromptsStarted, fetchCalculatorPromptsSuccessful, savePromptOrderFailed, savePromptOrderSuccessful, setCalculatorManagerPage, setCalculatorPromptUpdated, setFieldTypeUpdated, setPadOptionUpdated, upsertCalculatorFieldTypeFailed, upsertCalculatorFieldTypeSuccessful, upsertCalculatorPadOptionFailed, upsertCalculatorPadOptionSuccessful, upsertCalculatorPromptFailed, upsertCalculatorPromptSuccessful } from './actions';
import { initialCalculatorPrompt } from './reducer';
import { getAllCalculatorPrompts, getCalculatorPromptToDelete, getCurrentCalculatorFieldType, getCurrentCalculatorPromptOrderForType, getCurrentPadOption, getSavedCalculatorFieldType, getSavedPadOption, getSelectedCalculatorPrompt } from './selectors';
import { CalculatorActionTypes, CalculatorFieldTypeDB, CalculatorFieldTypeUI, CalculatorManagerPage, CalculatorPadOptionDB, CalculatorPadOptionUI, CalculatorPromptDB, CalculatorPromptPriorityOrder, CalculatorPromptUI } from './types';

export function* attemptFetchCalculatorPrompts({ payload }: ReturnType<typeof fetchCalculatorPromptsStarted>) {
    try {
        const calculatorPrompts: CalculatorPromptDB[] = yield call(fetchCalculatorPrompts, { clientId: payload });
        yield put(fetchCalculatorPromptsSuccessful(calculatorPrompts));
    } catch (e) {
        yield put(fetchCalculatorPromptsFailed((e as Error).message));
    }
}

function* fetchCalculatorPromptsWatcher() {
    yield takeLeading(CalculatorActionTypes.FETCH_CALCULATOR_PROMPT_EXAMPLES_STARTED, attemptFetchCalculatorPrompts);
}

export function* attemptUpsertCalculatorPrompt() {
    try {
        const calculatorPrompt: CalculatorPromptUI = yield select(getSelectedCalculatorPrompt);
        const calculatorPrompts: CalculatorPromptDB[] = yield call(upsertCalculatorPrompt, calculatorPrompt);
        yield put(upsertCalculatorPromptSuccessful(calculatorPrompts));
    } catch (e) {
        yield put(upsertCalculatorPromptFailed((e as Error).message));
    }
}

function* upsertCalculatorPromptWatcher() {
    yield takeLatest(CalculatorActionTypes.UPSERT_CALCULATOR_PROMPT_STARTED, attemptUpsertCalculatorPrompt);
}

export function* attemptDeleteCalculatorPrompt() {
    try {
        let updatedCalculatorPrompts: CalculatorPromptDB[] = [];
        const calculatorPromptToDelete: CalculatorPromptDB | null = yield select(getCalculatorPromptToDelete);
        if (!isNull(calculatorPromptToDelete)) {
            const { promptId, calculatorFieldType, priorityOrder } = calculatorPromptToDelete;
            const calculatorPrompts: CalculatorPromptDB[] =yield call(deleteCalculatorPrompt, { promptId, calculatorFieldType, priorityOrder });
            updatedCalculatorPrompts = calculatorPrompts;
        }
        yield put(deleteCalculatorPromptSuccessful(updatedCalculatorPrompts));
    } catch (e) {
        yield put(deleteCalculatorPromptFailed((e as Error).message));
    }
}

function* deleteCalculatorPromptWatcher() {
    yield takeLatest(CalculatorActionTypes.DELETE_CALCULATOR_PROMPT_STARTED, attemptDeleteCalculatorPrompt);
}

export function* checkUpdatedCalculatorPrompt() {
    const selectedPrompt: CalculatorPromptUI = yield select(getSelectedCalculatorPrompt);
    const existingPrompts: CalculatorPromptDB[] = yield select(getAllCalculatorPrompts);
    if (!isUndefined(selectedPrompt.promptId)) {
        const existingPrompt = existingPrompts.find(({ promptId }) => promptId === selectedPrompt.promptId);
        if (existingPrompt) {
            const strippedExistingPrompt = flow(
                unset('createdBy'),
                unset('createdByUsername'),
                unset('createdDate')
            )(existingPrompt);
            const hasUpdated = !isEqual(strippedExistingPrompt, selectedPrompt);
            yield put(setCalculatorPromptUpdated(hasUpdated));
        }
    } else {
        const hasUpdated = !isEqual(selectedPrompt, initialCalculatorPrompt);
        yield put(setCalculatorPromptUpdated(hasUpdated));
    }
}

function* checkUpdatedCalculatorPromptWatcher() {
    yield debounce(200, CalculatorActionTypes.UPDATE_SELECTED_CALCULATOR_PROMPT, checkUpdatedCalculatorPrompt);
}

export function* redirectCalculatorManagerTab({ payload }: ReturnType<typeof setCalculatorManagerPage>) {
    if (payload !== CalculatorManagerPage.SELECT) {
        const navigationPath = `/system/calculator/${payload === CalculatorManagerPage.PROMPTS ? 'prompts' : 'manager'}`;
        yield put(push(navigationPath));
    }
}

function* redirectCalculatorManagerTabWatcher() {
    yield takeLeading(CalculatorActionTypes.SET_CALCULATOR_MANAGER_PAGE, redirectCalculatorManagerTab);
}

export function* checkUpdatedCalculatorPadOption() {
    const currentPadOption: CalculatorPadOptionUI = yield select(getCurrentPadOption);
    const savedPadOption: CalculatorPadOptionUI = yield select(getSavedPadOption);
    const hasUpdated = !isEqual(currentPadOption, savedPadOption);
    yield put(setPadOptionUpdated(hasUpdated));
}

function* checkUpdatedCalculatorPadOptionWatcher() {
    yield debounce(200, CalculatorActionTypes.UPDATE_CALCULATOR_PAD_OPTION_VALUE, checkUpdatedCalculatorPadOption);
}

export function* attemptUpsertCalculatorPadOption() {
    try {
        const calculatorPadOption: CalculatorPadOptionUI = yield select(getCurrentPadOption);
        const calculatorPadOptions: CalculatorPadOptionDB[] = yield call(upsertCalculatorPadOption, calculatorPadOption);
        yield put(upsertCalculatorPadOptionSuccessful(calculatorPadOptions));
        toast(`Successfully saved calculator pad option - ${calculatorPadOption.label}`);
    } catch (e) {
        toast.error('Unable to save your calculator pad option. Please try again.');
        yield put(upsertCalculatorPadOptionFailed((e as Error).message));
    }
}

function* upsertCalculatorPadOptionWatcher() {
    yield takeLatest(CalculatorActionTypes.UPSERT_CALCULATOR_PAD_OPTION_STARTED, attemptUpsertCalculatorPadOption);
}

export function* attemptUpsertCalculatorFieldType() {
    try {
        const calculatorFieldType: CalculatorFieldTypeUI = yield select(getCurrentCalculatorFieldType);
        const calculatorFieldTypes: CalculatorFieldTypeDB[] = yield call(upsertCalculatorFieldType, calculatorFieldType);
        yield put(upsertCalculatorFieldTypeSuccessful(calculatorFieldTypes));
        toast(`Successfully saved calculator field - ${calculatorFieldType.name}`);
    } catch (e) {
        toast.error('Unable to save your calculator field. Please try again.');
        yield put(upsertCalculatorFieldTypeFailed((e as Error).message));
    }
}

function* upsertCalculatorFieldTypeWatcher() {
    yield takeLatest(CalculatorActionTypes.UPSERT_CALCULATOR_FIELD_TYPE_STARTED, attemptUpsertCalculatorFieldType);
}

export function* checkUpdatedCalculatorFieldType() {
    const currentCalculatorFieldType: CalculatorFieldTypeUI = yield select(getCurrentCalculatorFieldType);
    const savedCalculatorFieldType: CalculatorFieldTypeUI = yield select(getSavedCalculatorFieldType);
    const hasUpdated = !isEqual(currentCalculatorFieldType, savedCalculatorFieldType);
    yield put(setFieldTypeUpdated(hasUpdated));
}

function* checkUpdatedCalculatorFieldTypeWatcher() {
    yield debounce(200, CalculatorActionTypes.UPDATE_CALCULATOR_FIELD_TYPE_VALUE, checkUpdatedCalculatorFieldType);
}

export function* attemptReorderCalculatorPrompts() {
    try {
        const currentPromptOrder: CalculatorPromptDB[] = yield select(getCurrentCalculatorPromptOrderForType);
        const promptOrder: CalculatorPromptPriorityOrder[] = currentPromptOrder.map(({ promptId }, index) => ({ promptId, priorityOrder: index + 1 }));
        const calculatorPrompts: CalculatorPromptDB[] = yield call(updateCalculatorPromptPriorityOrder, { promptOrder });
        yield put(savePromptOrderSuccessful(calculatorPrompts));
    } catch (e) {
        toast.error('Unable to save the prompt order. Please try again.');
        yield put(savePromptOrderFailed((e as Error).message));
    }
}

function* reorderCalculatorPromptsWatcher() {
    yield takeLeading(CalculatorActionTypes.SAVE_PROMPT_ORDER_STARTED, attemptReorderCalculatorPrompts);
}

export function* calculatorSaga() {
    yield all([
        fork(fetchCalculatorPromptsWatcher),
        fork(upsertCalculatorPromptWatcher),
        fork(deleteCalculatorPromptWatcher),
        fork(checkUpdatedCalculatorPromptWatcher),
        fork(redirectCalculatorManagerTabWatcher),
        fork(checkUpdatedCalculatorPadOptionWatcher),
        fork(upsertCalculatorPadOptionWatcher),
        fork(checkUpdatedCalculatorFieldTypeWatcher),
        fork(upsertCalculatorFieldTypeWatcher),
        fork(reorderCalculatorPromptsWatcher)
    ]);
}
